import { useTranslation } from 'react-i18next';

import {
  AggregationIntervall,
  AggregationIntervalls,
  aggregationIntervallToString,
  AggregationType,
} from '@/features/charts/components/create-chart-type-selection';
import {
  calculateChartStats,
  ChartData,
} from '@/features/charts/util/chart-util';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import FilterIcon from '@/shared/assets/filter.svg?react';
import GrowthRateIcon from '@/shared/assets/growth_rate.svg?react';
import ValueDownIcon from '@/shared/assets/value-down.svg?react';
import ValueMiddleIcon from '@/shared/assets/value-middle.svg?react';
import ValueUpIcon from '@/shared/assets/value-up.svg?react';
import OneItemHighlighter from '@/shared/components/card-elements/one-item-highlighter';
import TendencyIndicator from '@/shared/components/tendency-indicator';
import { Gradients } from '@/shared/components/theme/gradient';
import { Button } from '@/shared/components/ui/button';
import { CardDescription, CardTitle } from '@/shared/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import RoundedIcon from '@/shared/components/ui/rounded-icon';
import { getKpiUnitAbbreviationTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { KpiUnit } from '@/shared/types/kpi';
import { formatNumberWithDots } from '@/shared/utils/format';

export type CustomChartHeaderProps = {
  title: string;
  description: string;
  showAggregationPeriod?: boolean;
  children?: React.ReactNode;
};

export const CustomChartHeader = ({
  title,
  description,
  children,
}: CustomChartHeaderProps) => {
  return (
    <div className="flex flex-row justify-between">
      <div>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
      {children}
    </div>
  );
};

export const MinMaxAverageInfoRow = ({
  chartData,
  aggregationType,
  unit,
}: {
  chartData: ChartData;
  aggregationType: string;
  unit?: string;
}) => {
  const { t: tChart } = useTranslation('chart');
  const { min, max, avg, changeRate } = calculateChartStats({
    chartData: chartData,
    aggregationType: aggregationType as AggregationType,
  });
  return (
    <div className="flex w-auto flex-row gap-3">
      <StatItem
        value={formatNumberWithDots(max)}
        info={tChart('chart_stats.max_info_label')}
        icon={<TendencyIndicator tendency="up" size="small" />}
        unit={unit}
      />
      <StatItem
        value={formatNumberWithDots(min)}
        info={tChart('chart_stats.min_info_label')}
        icon={<TendencyIndicator tendency="down" size="small" />}
        unit={unit}
      />
      <StatItem
        value={formatNumberWithDots(Math.round(avg * 10) / 10)}
        info={tChart('chart_stats.avg_info_label')}
        unit={unit}
        icon={
          <RoundedIcon
            className="!size-5"
            bgColor="bg-white/30"
            icon={<ValueMiddleIcon />}
          />
        }
      />
      <StatItem
        value={`${changeRate >= 0 ? '+' : ''}${changeRate.toFixed(1)}%`}
        info={tChart('chart_stats.change_rate_info_label')}
        icon={
          <RoundedIcon
            className="!size-5"
            bgColor="bg-white/30"
            icon={<GrowthRateIcon />}
          />
        }
      />
    </div>
  );
};

const StatItem = ({
  value,
  info,
  icon,
  unit,
}: {
  value: string;
  info: string;
  icon: React.ReactNode;
  unit?: string;
}) => {
  return (
    <div className="flex flex-row items-center gap-[6px] rounded-full border border-white/30 py-[6px] pl-[6px] pr-3">
      {icon}
      <InfoTextItem value={value} info={info} unit={unit} />
    </div>
  );
};

const InfoTextItem = ({
  value,
  info,
  unit,
}: {
  value: string;
  info: string;
  unit?: string;
}) => {
  return (
    <div className="flex flex-row items-center gap-1">
      <span className="text-4xs font-[590]">
        {value} {unit && unit}
      </span>
      <span className="hidden text-4xs text-custom-grey 2xl:block">{info}</span>
    </div>
  );
};

type RelativeInfoRowProps = {
  maxValue: number;
  maxInfo: string;
  currentValue: number;
  currentInfo: string;
  unit?: string;
};

export const RelativeInfoRow = ({
  maxValue,
  maxInfo,
  currentValue,
  currentInfo,
  unit,
}: RelativeInfoRowProps) => {
  return (
    <div className="flex w-auto flex-row gap-3">
      <StatItem
        value={formatNumberWithDots(currentValue)}
        info={currentInfo}
        unit={unit}
        icon={
          <RoundedIcon
            className="!size-5"
            bgColor="bg-white/30"
            icon={<ValueUpIcon />}
          />
        }
      />

      <StatItem
        value={formatNumberWithDots(maxValue)}
        info={maxInfo}
        unit={unit}
        icon={
          <RoundedIcon
            className="!size-5"
            bgColor="bg-white/30"
            icon={<ValueDownIcon />}
          />
        }
      />
    </div>
  );
};

export const AggregationIntervallSelection = ({
  aggregationIntervall,
  onIntervalChange,
}: {
  aggregationIntervall: AggregationIntervall;
  onIntervalChange: (intervall: AggregationIntervall) => void;
}) => {
  return (
    <div>
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            className="hover:bg-transparent hover:text-white"
            variant={'ghost'}
          >
            <OneItemHighlighter
              title={aggregationIntervallToString(aggregationIntervall)}
              subtitle="Intervall"
              icon={
                <RoundedIcon
                  bgColor={Gradients.purpleBlueGradient}
                  icon={<FilterIcon />}
                />
              }
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {AggregationIntervalls.map((period, index) => (
            <DropdownMenuItem key={index}>
              {
                <Button
                  onClick={() => {
                    onIntervalChange(period);
                  }}
                  variant={'ghost'}
                >
                  {aggregationIntervallToString(period)}
                </Button>
              }
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const ChartStats = ({
  chartData,
  aggregationType,
  maxValue,
  unit,
}: {
  chartData: ChartData;
  aggregationType: AggregationType;
  maxValue?: number;
  unit: string | KpiUnit | null;
}) => {
  const { t: tChart } = useTranslation('chart');
  const { max } = calculateChartStats({
    chartData: chartData,
    aggregationType: aggregationType,
  });

  const { tKpiTypes } = useKpiTranslations();
  const unitAbbreviation = getKpiUnitAbbreviationTranslation(tKpiTypes, unit);

  switch (aggregationType) {
    case AggregationType.ACCUMULATED:
      return (
        maxValue !== undefined &&
        maxValue > 0 && (
          <RelativeInfoRow
            maxValue={maxValue}
            maxInfo={tChart('chart_stats.max_value_label')}
            currentValue={max}
            currentInfo={tChart('chart_stats.current_value_label')}
            unit={unitAbbreviation}
          />
        )
      );
    case AggregationType.TIMELINE:
      return (
        <MinMaxAverageInfoRow
          key="min-max-average"
          chartData={chartData}
          aggregationType={aggregationType}
          unit={unitAbbreviation}
        />
      );
    default:
  }
};
